import {
  encodeParentPinHash,
  parseParentPinHash,
  PARENT_PIN_HASH_BYTES,
  PARENT_PIN_HASH_ITERATIONS,
  PARENT_PIN_SALT_BYTES
} from "@/platform/parent-access/pinHashFormat";

const PIN_PATTERN = /^\d{4}$/;

export class InvalidParentPinError extends Error {
  override readonly name = "InvalidParentPinError";

  constructor() {
    super("A parent PIN must contain exactly four digits.");
  }
}

export function isValidParentPin(pin: string): boolean {
  return PIN_PATTERN.test(pin);
}

export async function createParentPinHash(
  pin: string,
  webCrypto: Crypto = globalThis.crypto
): Promise<string> {
  assertValidPin(pin);

  const salt = webCrypto.getRandomValues(
    new Uint8Array(PARENT_PIN_SALT_BYTES)
  );
  const hash = await derivePinHash(pin, salt, webCrypto);

  return encodeParentPinHash(salt, hash);
}

export async function verifyParentPin(
  pin: string,
  encodedHash: string,
  webCrypto: Crypto = globalThis.crypto
): Promise<boolean> {
  if (!isValidParentPin(pin)) {
    return false;
  }

  const parsedHash = parseParentPinHash(encodedHash);
  if (parsedHash === undefined) {
    return false;
  }

  const candidateHash = await derivePinHash(pin, parsedHash.salt, webCrypto);
  return constantTimeEqual(candidateHash, parsedHash.hash);
}

function assertValidPin(pin: string): void {
  if (!isValidParentPin(pin)) {
    throw new InvalidParentPinError();
  }
}

async function derivePinHash(
  pin: string,
  salt: Uint8Array<ArrayBuffer>,
  webCrypto: Crypto
): Promise<Uint8Array<ArrayBuffer>> {
  const key = await webCrypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pin),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  const bits = await webCrypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      iterations: PARENT_PIN_HASH_ITERATIONS,
      salt
    },
    key,
    PARENT_PIN_HASH_BYTES * 8
  );

  return new Uint8Array(bits);
}

function constantTimeEqual(
  left: Uint8Array<ArrayBuffer>,
  right: Uint8Array<ArrayBuffer>
): boolean {
  if (left.length !== right.length) {
    return false;
  }

  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }

  return difference === 0;
}
